// Shared date/time formatting for both frontends (customer app + admin).
//
// Backend timestamps are UTC and are sometimes "naive" (no trailing 'Z'); these
// helpers append a UTC designator when one is missing so a value is never
// misread as local time, then render in the BROWSER'S locale and local timezone
// WITHOUT seconds. Route every user-facing date/time through one of these so the
// format stays consistent everywhere.

const PLACEHOLDER = '—'; // em dash

// Locale-aware, no seconds. `undefined` locale => the browser's locale; the
// values render in the browser's local timezone (the toLocale* default).
const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

// Bare ISO calendar date (YYYY-MM-DD), no time/zone component.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse an API timestamp robustly. Backend `*Utc` values are UTC; when the
 * string carries no timezone designator, append 'Z' so it isn't interpreted as
 * local time.
 */
export function parseApiDate(value: string): Date {
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTz ? value : `${value}Z`);
}

/** Date + time, browser locale + local timezone, no seconds. */
export function formatDateTime(value?: string | null, placeholder = PLACEHOLDER): string {
  if (!value) return placeholder;
  const d = parseApiDate(value);
  return Number.isNaN(d.getTime()) ? placeholder : d.toLocaleString(undefined, DATE_TIME_OPTS);
}

/** Time only, browser locale + local timezone, no seconds. */
export function formatTime(value?: string | null, placeholder = PLACEHOLDER): string {
  if (!value) return placeholder;
  const d = parseApiDate(value);
  return Number.isNaN(d.getTime()) ? placeholder : d.toLocaleTimeString(undefined, TIME_OPTS);
}

/**
 * Relative "time ago" label for recency-sensitive timestamps (a runner's last
 * check-in, a deployment's last run). Pass `now` (epoch ms) so the label can
 * advance on a ticking clock and stays deterministic in tests. Values a week or
 * older fall back to an absolute date+time, where "9 days ago" is less useful
 * than the actual date. Future values (clock skew) read as "just now".
 */
export function formatRelativeTime(
  value?: string | null,
  now: number = Date.now(),
  placeholder = PLACEHOLDER,
): string {
  if (!value) return placeholder;
  const t = parseApiDate(value).getTime();
  if (Number.isNaN(t)) return placeholder;
  // Floor each step so a label only advances once its boundary is actually
  // crossed (59m59s reads "59 min ago", not "1 hr ago").
  const sec = Math.floor((now - t) / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDateTime(value, placeholder);
}

/**
 * Date only, browser locale. Date-only values are conventionally "floating", so
 * this parses the string as-is (no UTC coercion) to avoid an off-by-one day
 * shift west of UTC.
 */
export function formatDate(value?: string | null, placeholder = PLACEHOLDER): string {
  if (!value) return placeholder;
  // A bare calendar date parses as UTC midnight, which `toLocaleDateString`
  // would render as the previous day west of UTC. Build it in local time so the
  // displayed day matches the literal string.
  if (DATE_ONLY_RE.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, DATE_OPTS);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? placeholder : d.toLocaleDateString(undefined, DATE_OPTS);
}
